// ── Discovery Deck ──────────────────────────────────────────
// 5-card flip overlay shown when the novelty engine completes.
// Each card answers ONE question the user naturally asks after a run.
//
// Card order (from highest leverage to lowest):
//   1. THE FRESHEST IDEA       → top blend or top-novelty base node
//   2. THE HIDDEN ASSUMPTION   → strongest counterfactual
//   3. THE MISSED ANALOG       → strongest case-based reference
//   4. THE CONSTRAINT TO DROP  → strongest constraint-relaxer transformation
//   5. THE UNEXPLORED REGION   → largest empty area of the QD map
//
// Auto-dismisses on first interaction outside; user can also skip cards.

import React, { useState, useMemo, useEffect } from 'react';
import { useNovelty, noveltyColor, noveltyLabel } from './NoveltyContext';

export default function DiscoveryDeck({ visible, onDismiss, onShowInTree }) {
  const novelty = useNovelty();
  const [idx, setIdx] = useState(0);

  // Build deck from current novelty data
  const cards = useMemo(() => buildDeck(novelty), [novelty]);

  useEffect(() => { if (visible) setIdx(0); }, [visible]);

  if (!visible || cards.length === 0) return null;
  const card = cards[idx];
  const isLast = idx === cards.length - 1;

  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeIn 0.3s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(180deg, #181828 0%, #0f0f1a 100%)',
          border: '1px solid #2a2a4a',
          borderRadius: 12,
          padding: 24,
          width: 560,
          maxWidth: '90vw',
          maxHeight: '85vh',
          boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 80px rgba(108,99,255,0.2)',
          fontFamily: 'var(--font-mono)',
          color: '#ddd',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 11, color: '#888' }}>
          <span>🧬 novelty engine · run complete</span>
          <button
            onClick={() => saveRunAsPattern(novelty)}
            style={{
              marginLeft: 'auto', background: 'transparent', border: '1px solid #2a2a4a',
              color: '#a78bfa', padding: '3px 8px', borderRadius: 4, fontSize: 10,
              cursor: 'pointer', fontFamily: 'var(--font-mono)',
            }}
            title="Save this run's configuration as a reusable pattern"
          >
            💾 Save as pattern
          </button>
          <span style={{ color: '#555' }}>{idx + 1} of {cards.length}</span>
          <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: 14, padding: '0 6px' }} title="Close (Esc)">✕</button>
        </div>

        {/* Card category */}
        <div style={{ fontSize: 10, color: '#a78bfa', letterSpacing: 1, marginBottom: 4 }}>{card.category}</div>

        {/* Card title */}
        <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 14 }}>{card.title}</div>

        {/* Card body */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 100 }}>
          {card.body}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 12, borderTop: '1px solid #2a2a4a' }}>
          {card.actions?.map((a, i) => (
            <button
              key={i}
              onClick={() => { a.onClick?.({ onShowInTree, novelty, onDismiss }); }}
              style={{
                background: a.primary ? '#6c63ff' : 'transparent',
                color: a.primary ? '#fff' : '#aaa',
                border: a.primary ? 'none' : '1px solid #333',
                padding: '8px 14px',
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                fontWeight: 600,
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {a.label}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          {idx > 0 && (
            <button onClick={() => setIdx(idx - 1)} style={navBtn}>← prev</button>
          )}
          {!isLast ? (
            <button onClick={() => setIdx(idx + 1)} style={navBtnPrimary}>next →</button>
          ) : (
            <button onClick={onDismiss} style={navBtnPrimary}>done</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Save-as-pattern helper ────────────────────────────────
// Builds a runSignature from current novelty context and posts to the
// save-from-novelty-run endpoint. See features/patterns/save-as-pattern.feature
async function saveRunAsPattern(novelty) {
  const { dmadResults, archive, recombinations, evolveGenerations } = novelty;
  // Find champion if Phase C ran
  const allCands = Object.values(evolveGenerations || {}).flat();
  const champion = [...allCands].sort((a, b) => (b.fitness || 0) - (a.fitness || 0))[0];

  const defaultName = champion
    ? `Workflow that surfaced "${(champion.label || '').slice(0, 40)}"`
    : `Novelty run ${new Date().toLocaleDateString()}`;

  // eslint-disable-next-line no-alert
  const patternName = window.prompt('Save this run as a pattern.\nGive it a name:', defaultName);
  if (!patternName) return;
  // eslint-disable-next-line no-alert
  const description = window.prompt('Optional description (1 line):', '') || '';

  const runSignature = {
    reasonersUsed: dmadResults?.map(d => (d.label || '').toLowerCase().split(' ')[0]).filter(Boolean),
    qdDimensions: archive?.dimensions || null,
    recombineCount: recombinations?.length || 5,
    evolveConfig: champion ? {
      generations: Math.max(...Object.keys(evolveGenerations || {}).map(Number)),
      offspringPerGen: 4,
      mutateRatio: 0.5,
    } : null,
    evaluatorIds: champion ? ['falsifiability', 'antipattern', 'marketsim'] : null,
  };

  try {
    const res = await fetch('/api/patterns/save-from-novelty-run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patternName,
        description,
        runSignature,
        championLabel: champion?.label || null,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      // eslint-disable-next-line no-alert
      window.alert(`Save failed: ${err.error || res.statusText}`);
      return;
    }
    const { pattern } = await res.json();
    // eslint-disable-next-line no-alert
    window.alert(`✅ Saved as pattern "${pattern.name}".\nReplay it from the patterns library on your next prompt.`);
  } catch (err) {
    // eslint-disable-next-line no-alert
    window.alert(`Save failed: ${err.message}`);
  }
}

const navBtn = {
  background: 'transparent',
  color: '#888',
  border: '1px solid #333',
  padding: '8px 14px',
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  fontWeight: 600,
  borderRadius: 6,
  cursor: 'pointer',
};
const navBtnPrimary = { ...navBtn, background: '#2a1f4a', borderColor: '#6c63ff', color: '#fff' };

// ── Card builders ─────────────────────────────────────────
function buildDeck({ scores, archive, recombinations, blendScores, dmadResults }) {
  const cards = [];

  // 1. The freshest idea — highest-novelty blend (or top base node if no blends)
  const blendsRanked = recombinations
    .filter(r => !r.error)
    .map((r, i) => ({ ...r, _id: `blend-${i}`, _score: blendScores[`blend-${i}`]?.score }))
    .sort((a, b) => (b._score ?? 0) - (a._score ?? 0));
  const topBlend = blendsRanked[0];
  const baseEntries = Object.entries(scores).filter(([, v]) => typeof v.score === 'number');
  const topBase = baseEntries.sort((a, b) => b[1].score - a[1].score)[0];
  if (topBlend) {
    cards.push({
      category: 'THE FRESHEST IDEA WE FOUND',
      title: topBlend.label,
      body: (
        <div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 12 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: noveltyColor(topBlend._score), lineHeight: 1 }}>{topBlend._score ?? '—'}</span>
            <span style={{ fontSize: 11, color: '#888' }}>/100 · {noveltyLabel(topBlend._score)}</span>
          </div>
          <div style={{ fontSize: 11, color: '#a78bfa', marginBottom: 8, fontStyle: 'italic' }}>
            {topBlend.blendStrategy}
          </div>
          <div style={{ fontSize: 13, color: '#ccc', marginBottom: 12 }}>{topBlend.reasoning}</div>
          <div style={{ fontSize: 11, color: '#666', paddingLeft: 8, borderLeft: '2px solid #2a2a4a' }}>
            <div><strong style={{ color: '#888' }}>Inherits from A:</strong> {topBlend.inheritsFrom?.a}</div>
            <div><strong style={{ color: '#888' }}>Inherits from B:</strong> {topBlend.inheritsFrom?.b}</div>
            <div style={{ marginTop: 6, color: '#444' }}>parents: {topBlend.parents?.map(p => `"${(p.label||'').slice(0,28)}"`).join(' × ')}</div>
          </div>
        </div>
      ),
      actions: [
        { label: '🔬 drill into this', primary: true, onClick: ({ novelty, onDismiss }) => { novelty.onDrillNovelty?.(topBlend.label, topBlend.reasoning, 'blend'); onDismiss?.(); } },
      ],
    });
  } else if (topBase) {
    const [, info] = topBase;
    cards.push({
      category: 'THE MOST NOVEL IDEA',
      title: info.closestExisting ? `vs "${info.closestExisting}"` : 'Top novelty score',
      body: (
        <div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 12 }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: noveltyColor(info.score) }}>{info.score}</span>
            <span style={{ fontSize: 11, color: '#888' }}>/100 · {noveltyLabel(info.score)}</span>
          </div>
          {info.whyNovel && <div style={{ fontSize: 13, color: '#ccc' }}>{info.whyNovel}</div>}
        </div>
      ),
    });
  }

  // 2. The hidden assumption — counterfactual
  const cf = dmadResults.find(r => r.id === 'counterfactual' && !r.error);
  if (cf?.output?.critique) {
    cards.push({
      category: 'THE HIDDEN ASSUMPTION',
      title: 'What if this isn\'t true?',
      body: (
        <div>
          <div style={{ fontSize: 14, color: '#ddd', marginBottom: 14, fontStyle: 'italic' }}>
            "{cf.output.critique}"
          </div>
          <div style={{ fontSize: 11, color: '#a78bfa', marginBottom: 8 }}>If you accept this, the engine suggests:</div>
          {(cf.output.transformations || []).slice(0, 1).map((t, i) => (
            <div key={i} style={{ background: '#1a1a2a', borderRadius: 6, padding: 10, fontSize: 12 }}>
              <div style={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}>{t.what}</div>
              <div style={{ color: '#34d399', fontSize: 11 }}>→ {t.expectedShift}</div>
            </div>
          ))}
        </div>
      ),
    });
  }

  // 3. The missed analog — case-based
  const cb = dmadResults.find(r => r.id === 'case-based' && !r.error);
  if (cb?.output?.critique) {
    cards.push({
      category: 'THE HISTORICAL ANALOG',
      title: 'Has someone tried this before?',
      body: (
        <div>
          <div style={{ fontSize: 14, color: '#ddd', marginBottom: 14, fontStyle: 'italic' }}>
            "{cb.output.critique}"
          </div>
          {(cb.output.transformations || []).slice(0, 2).map((t, i) => (
            <div key={i} style={{ background: '#1a1a2a', borderRadius: 6, padding: 10, fontSize: 12, marginBottom: 6 }}>
              <div style={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}>{t.what}</div>
              {t.why && <div style={{ color: '#888', fontSize: 11 }}>↳ {t.why}</div>}
            </div>
          ))}
        </div>
      ),
    });
  }

  // 4. Constraint to drop
  const cr = dmadResults.find(r => r.id === 'constraint-relaxer' && !r.error);
  if (cr?.output?.transformations?.length) {
    const top = cr.output.transformations[0];
    cards.push({
      category: 'THE CONSTRAINT TO DROP',
      title: cr.output.critique?.slice(0, 80) || 'What constraint is everyone assuming?',
      body: (
        <div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>{cr.output.critique}</div>
          <div style={{ background: '#1a1a2a', borderRadius: 6, padding: 12 }}>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{top.what}</div>
            {top.why && <div style={{ color: '#888', fontSize: 11, marginBottom: 4 }}>↳ {top.why}</div>}
            {top.expectedShift && <div style={{ color: '#34d399', fontSize: 12, fontWeight: 600 }}>→ {top.expectedShift}</div>}
          </div>
        </div>
      ),
    });
  }

  // 5. Unexplored region — largest empty area of QD map
  if (archive?.stats) {
    const { stats, dimensions } = archive;
    const unfilled = stats.totalCells - stats.filledCells;
    cards.push({
      category: 'THE UNEXPLORED REGION',
      title: `${unfilled} cells of the design space have no ideas yet`,
      body: (
        <div>
          <div style={{ fontSize: 12, color: '#bbb', marginBottom: 12 }}>
            You filled <strong style={{ color: '#fff' }}>{stats.filledCells} of {stats.totalCells}</strong> cells ({Math.round(stats.coverage * 100)}% coverage).
          </div>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
            The design space is built from {Object.keys(dimensions || {}).length} dimensions:
          </div>
          <div style={{ fontSize: 11, color: '#666', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {Object.entries(dimensions || {}).map(([dim, bins]) => (
              <div key={dim}><strong style={{ color: '#888' }}>{dim}:</strong> {bins.join(', ')}</div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#a78bfa', marginTop: 12 }}>
            Most ideas cluster in similar cells. The empty regions are where genuinely new framings live.
          </div>
        </div>
      ),
      actions: [
        { label: '🗺 see the map', primary: true, onClick: ({ onShowInTree, onDismiss }) => { onShowInTree?.('map'); onDismiss?.(); } },
      ],
    });
  }

  return cards;
}
